import React, {useMemo} from 'react';
import {Controller, useForm} from 'react-hook-form';
import {Button, CircularProgress, Grid, TextField} from '@material-ui/core';
import Autocomplete from '@material-ui/lab/Autocomplete';
import {Skeleton} from '@material-ui/lab';

import {
  IFarmInputProduct,
  IFarmInputProgramLineInput,
} from 'constants/interfaces';

import './ProgramForm.scss';

export function ProgramLineForm(props: {
  programLine?: IFarmInputProgramLineInput;
  isLoadingStages: boolean;
  stages: string[];
  isLoadingProducts: boolean;
  allProducts: IFarmInputProduct[];
  isLoadingApplicationUnits: boolean;
  applicationUnits: string[];
  isLoadingApplicationTypes: boolean;
  applicationTypes: string[];
  isLoading: boolean;
  submitButtonTitle: string;
  onSubmit: (programLine: IFarmInputProgramLineInput) => any;
  cancelButtonTitle?: string;
  onCancel?: () => any;
  smallWidth?: boolean;
  readonly?: boolean;
}) {
  const {
    isLoading,
    isLoadingStages,
    stages,
    isLoadingProducts,
    allProducts,
    isLoadingApplicationUnits,
    applicationUnits,
    isLoadingApplicationTypes,
    applicationTypes,
  } = props;

  type Submission = IFarmInputProgramLineInput;

  const {handleSubmit, control, watch, setValue} = useForm<Submission>({
    defaultValues: {
      id: props.programLine?.id,
      programId: props.programLine?.programId,
      productId: props.programLine?.productId,
      when: props.programLine?.when,
      quantity: props.programLine?.quantity,
      PHI: props.programLine?.PHI,
      applicationRate: props.programLine?.applicationRate,
      applicationUnit: props.programLine?.applicationUnit,
      applicationType: props.programLine?.applicationType,
      applicationTypePerHectare: props.programLine?.applicationTypePerHectare,
      comment: props.programLine?.comment,
      price: props.programLine?.price,
    },
  });

  const onSubmit = (submission: Submission) => {
    const cleanedPrice =
      submission.price !== undefined && submission.price !== null
        ? Number(submission.price)
        : undefined;

    props.onSubmit({
      ...submission,
      quantity: parseFloat(submission.quantity as any),
      price: cleanedPrice,
      PHI: submission.PHI ? parseFloat(submission.PHI as any) : undefined,
      applicationRate: parseFloat(submission.applicationRate as any),
      applicationTypePerHectare:
        submission.applicationType === 'Per Hectare'
          ? 1
          : parseFloat(submission.applicationTypePerHectare as any),
    });
  };

  const findProduct = (id: any) => allProducts?.find((prod) => prod.id === id);

  const getApplicationTypePerHectareLabel = (t?: string) => {
    switch (t) {
      case 'Per Tree':
        return 'Trees Per Hectare';
      case 'Per 100ml':
        return '100ml Per Hectare';
      default:
        return 'Application Type Per Hectare';
    }
  };

  const applicationType = watch('applicationType');
  const isPerHectare = applicationType === 'Per Hectare';
  const applicationTypePerHectareLabel = useMemo(
    () => getApplicationTypePerHectareLabel(applicationType),
    [applicationType],
  );

  return (
    <div className="program-line-form-container">
      <div className="form-container">
        <form className="form" onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={2} className="form-section">
            {/* Stage */}
            <Grid item xs={12} className="form-row">
              <label>Stage</label>
              {isLoadingStages ? (
                <Skeleton variant="rect" height={54} className="input" />
              ) : (
                <Controller
                  name="when"
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <Autocomplete
                      id="when"
                      options={stages || []}
                      disabled={props.readonly || isLoadingStages || isLoading}
                      size="small"
                      value={value}
                      onChange={(_, type) => onChange(type)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className="input"
                          label="Stage"
                          variant="outlined"
                          fullWidth
                          error={!!error}
                          helperText={error?.message || null}
                          disabled={
                            props.readonly || isLoadingStages || isLoading
                          }
                          size="medium"
                        />
                      )}
                    />
                  )}
                  rules={{required: 'Stage required'}}
                />
              )}
            </Grid>

            {/* Product */}
            <Grid item xs={12} className="form-row">
              <label>Product</label>
              {isLoadingProducts ? (
                <Skeleton variant="rect" height={54} className="input" />
              ) : (
                <Controller
                  name="productId"
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <Autocomplete
                      id="product"
                      options={allProducts || []}
                      disabled={
                        props.readonly || isLoadingProducts || isLoading
                      }
                      getOptionLabel={(p: IFarmInputProduct) => p.name}
                      size="small"
                      value={findProduct(value)}
                      onChange={(_, prod) => onChange(prod?.id)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className="input"
                          label="Product"
                          variant="outlined"
                          fullWidth
                          error={!!error}
                          helperText={error?.message || null}
                          disabled={
                            props.readonly || isLoadingProducts || isLoading
                          }
                          size="medium"
                        />
                      )}
                    />
                  )}
                  rules={{required: 'Product required'}}
                />
              )}
            </Grid>

            {/* Application Rate */}
            <Grid item xs={12} className="form-row">
              <label>Application Rate</label>
              <Controller
                name="applicationRate"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    variant="outlined"
                    fullWidth
                    type="number"
                    label="Application Rate"
                    InputProps={{inputProps: {min: 0.01, step: 0.01}}}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message || null}
                    disabled={props.readonly || isLoading}
                  />
                )}
                rules={{
                  required: 'Application rate required',
                  validate: (v) =>
                    !isNaN(v!) && parseFloat(v as any) > 0
                      ? true
                      : 'Positive number required',
                }}
              />
            </Grid>

            {/* Application Unit */}
            <Grid item xs={12} className="form-row">
              <label>Application Unit</label>
              {isLoadingApplicationUnits ? (
                <Skeleton variant="rect" height={54} className="input" />
              ) : (
                <Controller
                  name="applicationUnit"
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <Autocomplete
                      id="applicationUnit"
                      options={applicationUnits || []}
                      disabled={
                        props.readonly || isLoadingApplicationUnits || isLoading
                      }
                      size="small"
                      value={value}
                      onChange={(_, type) => onChange(type)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className="input"
                          variant="outlined"
                          fullWidth
                          label="Application Unit"
                          error={!!error}
                          helperText={error?.message || null}
                          disabled={
                            props.readonly ||
                            isLoadingApplicationUnits ||
                            isLoading
                          }
                          size="medium"
                        />
                      )}
                    />
                  )}
                  rules={{required: 'Application unit required'}}
                />
              )}
            </Grid>

            {/* Application Type */}
            <Grid item xs={12} className="form-row">
              <label>Application Type</label>
              {isLoadingApplicationTypes ? (
                <Skeleton variant="rect" height={54} className="input" />
              ) : (
                <Controller
                  name="applicationType"
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <Autocomplete
                      id="applicationType"
                      options={applicationTypes || []}
                      disabled={
                        props.readonly || isLoadingApplicationTypes || isLoading
                      }
                      size="small"
                      value={value}
                      onChange={(_, type) => {
                        onChange(type);
                        if (type === 'Per Hectare') {
                          setValue('applicationTypePerHectare', 1);
                        } else {
                          setValue('applicationTypePerHectare', undefined);
                        }
                      }}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          className="input"
                          variant="outlined"
                          fullWidth
                          label="Application Type"
                          error={!!error}
                          helperText={error?.message || null}
                          disabled={
                            props.readonly ||
                            isLoadingApplicationTypes ||
                            isLoading
                          }
                          size="medium"
                        />
                      )}
                    />
                  )}
                  rules={{required: 'Application type required'}}
                />
              )}
            </Grid>

            {/* Application Type per Hectare (conditional) */}
            {!isPerHectare && (
              <Grid item xs={12} className="form-row">
                <label>{applicationTypePerHectareLabel}</label>
                <Controller
                  name="applicationTypePerHectare"
                  control={control}
                  render={({field: {onChange, value}, fieldState: {error}}) => (
                    <TextField
                      className="input"
                      variant="outlined"
                      fullWidth
                      type="number"
                      label={applicationTypePerHectareLabel}
                      InputProps={{inputProps: {min: 0.01, step: 0.01}}}
                      value={value}
                      onChange={onChange}
                      error={!!error}
                      helperText={error?.message || null}
                      disabled={props.readonly || isLoading}
                    />
                  )}
                  rules={{
                    required: 'Required',
                    validate: (v) =>
                      !isNaN(v!) && parseFloat(v as any) > 0
                        ? true
                        : 'Positive number required',
                  }}
                />
              </Grid>
            )}

            {/* Price (override) */}
            <Grid item xs={12} className="form-row">
              <label>Price (override)</label>
              <Controller
                name="price"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    variant="outlined"
                    fullWidth
                    type="number"
                    label="Price  (R/unit)"
                    InputProps={{inputProps: {min: 0.01, step: 0.01}}}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message || null}
                    disabled={props.readonly || isLoading}
                  />
                )}
                rules={{
                  validate: (v) =>
                    v == null
                      ? true
                      : !isNaN(v as any) && parseFloat(v as any) > 0
                      ? true
                      : 'Positive number required',
                }}
              />
            </Grid>

            {/* Quantity */}
            <Grid item xs={12} className="form-row">
              <label>Quantity</label>
              <Controller
                name="quantity"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    variant="outlined"
                    fullWidth
                    type="number"
                    label="Quantity"
                    InputProps={{inputProps: {min: 0.01, step: 0.01}}}
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message || null}
                    disabled={props.readonly || isLoading}
                  />
                )}
                rules={{
                  required: 'Quantity required',
                  validate: (v) =>
                    !isNaN(v!) && parseFloat(v as any) > 0
                      ? true
                      : 'Positive number required',
                }}
              />
            </Grid>

            {/* Comment */}
            <Grid item xs={12} className="form-row">
              <label>Comment</label>
              <Controller
                name="comment"
                control={control}
                render={({field: {onChange, value}, fieldState: {error}}) => (
                  <TextField
                    className="input"
                    variant="outlined"
                    fullWidth
                    label="Comment"
                    value={value}
                    onChange={onChange}
                    error={!!error}
                    helperText={error?.message || null}
                    disabled={props.readonly || isLoading}
                  />
                )}
              />
            </Grid>
          </Grid>

          {!props.readonly && (
            <Grid container spacing={2} style={{marginTop: '1rem'}}>
              <Grid item xs={12} sm={6}>
                {props.cancelButtonTitle && (
                  <Button
                    className="secondary-btn"
                    variant="contained"
                    fullWidth
                    disableElevation
                    disabled={props.isLoading || isLoadingProducts}
                    onClick={props.onCancel}
                  >
                    {props.cancelButtonTitle}
                  </Button>
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                <Button
                  className="primary-btn"
                  type="submit"
                  variant="contained"
                  fullWidth
                  disableElevation
                  color="primary"
                  disabled={props.isLoading || isLoadingProducts}
                >
                  {props.isLoading ? (
                    <CircularProgress color="primary" size={24} thickness={6} />
                  ) : (
                    props.submitButtonTitle
                  )}
                </Button>
              </Grid>
            </Grid>
          )}
        </form>
      </div>
    </div>
  );
}
